/**
 * UnassignedClientRequests.jsx — Phase 84.5
 *
 * Standalone banner shown at the top of the /projects page listing any
 * incoming client_requests that have not been attached to a project yet
 * (typically brand-new project briefs). Lets a CM/admin assign a request
 * to an existing project in one click so the thread moves under
 * ProjectClientRequestsSection.
 *
 * When zero orphan requests exist, the banner is not rendered.
 *
 * Replaces the standalone "Client inbox" page that Phase 84.4/84.5 retired.
 */
import React, { useCallback, useEffect, useMemo, useState } from "react";
import api, { formatApiError } from "@/lib/api";
import { toast } from "sonner";
import { Inbox, ChevronRight, ChevronDown, Sparkles } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import usePermissions from "@/lib/usePermissions";

export default function UnassignedClientRequests({ projects = [] }) {
  const can = usePermissions();
  const [rows, setRows] = useState(null);
  // Feb 2026 · Global Fixes P2 — start COLLAPSED so this banner never blows
  // the project page's scroll height. User expands with a click and the
  // list is bounded to a scrollable card body.
  const [open, setOpen] = useState(false);
  const [expandedId, setExpandedId] = useState(null);
  const [assigning, setAssigning] = useState(null);
  const [pick, setPick] = useState({});  // requestId → chosen project id

  const load = useCallback(async () => {
    try {
      const { data } = await api.get("/client-requests");
      const orphans = (data || []).filter((r) => !r.project_id && r.status !== "converted" && r.status !== "declined");
      setRows(orphans);
    } catch (e) {
      setRows([]);
      // Non-admins get a 403 here — silently hide, matches project section behaviour.
      const s = e?.response?.status;
      if (s && s !== 403) toast.error(formatApiError(e));
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  const canManage = can("client_inbox");

  const projectOptions = useMemo(
    () => (projects || []).filter((p) => p.status !== "completed"),
    [projects]
  );

  if (!rows || rows.length === 0 || !canManage) return null;

  const assign = async (r) => {
    const pid = pick[r.id];
    if (!pid) { toast.info("Pick a project first"); return; }
    setAssigning(r.id);
    try {
      await api.patch(`/client-requests/${r.id}`, { project_id: pid, status: r.status === "new" ? "assigned" : r.status });
      toast.success("Request attached to project");
      load();
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setAssigning(null); }
  };

  const decline = async (r) => {
    if (!window.confirm("Decline this client request?")) return;
    try {
      await api.patch(`/client-requests/${r.id}`, { status: "declined" });
      toast.success("Marked declined");
      load();
    } catch (e) { toast.error(formatApiError(e)); }
  };

  return (
    <div className="card-flat overflow-hidden mb-6" data-testid="unassigned-client-requests">
      <button
        type="button"
        onClick={() => setOpen(!open)}
        className="w-full px-5 py-3 flex items-center justify-between border-b border-slate-100 hover:bg-slate-50/50"
        data-testid="unassigned-cr-toggle"
      >
        <div className="flex items-center gap-2">
          <Inbox size={14} className="text-[#F54800]" />
          <span className="font-display font-semibold text-slate-900 text-sm">Unassigned client requests</span>
          <span className="text-[10px] text-white bg-[#F54800] px-1.5 py-0.5 rounded-sm" data-testid="unassigned-cr-count">
            {rows.length}
          </span>
        </div>
        {open ? <ChevronDown size={14} className="text-slate-400" /> : <ChevronRight size={14} className="text-slate-400" />}
      </button>
      {open && (
        <ul className="max-h-96 overflow-y-auto" data-testid="unassigned-cr-list">
          {rows.map((r) => {
            const isOpen = expandedId === r.id;
            return (
              <li key={r.id} className="border-b border-slate-50 last:border-0" data-testid={`unassigned-cr-row-${r.id}`}>
                <button
                  type="button"
                  onClick={() => setExpandedId(isOpen ? null : r.id)}
                  className="w-full px-5 py-3 text-left flex items-start gap-2 hover:bg-slate-50/60"
                  data-testid={`unassigned-cr-header-${r.id}`}
                >
                  {isOpen ? <ChevronDown size={13} className="text-slate-400 mt-1" /> : <ChevronRight size={13} className="text-slate-400 mt-1" />}
                  <div className="min-w-0 flex-1">
                    <div className="flex items-center gap-2 flex-wrap">
                      <Sparkles size={11} className="text-[#F54800]" />
                      <span className="font-medium text-sm text-slate-900 truncate">{r.title}</span>
                      <span className="text-[9px] uppercase tracking-wider bg-amber-100 text-amber-700 px-1.5 py-0.5 rounded-sm">{r.request_type || "new"}</span>
                      {r.business_unit && <span className="text-[9px] uppercase tracking-wider bg-slate-100 text-slate-600 px-1.5 py-0.5 rounded-sm">{r.business_unit}</span>}
                    </div>
                    <div className="text-[11px] text-slate-500 truncate mt-0.5">
                      {r.client_name}{r.client_company ? ` · ${r.client_company}` : ""}
                      {r.created_at ? ` · ${r.created_at.slice(0, 10)}` : ""}
                    </div>
                  </div>
                </button>
                {isOpen && (
                  <div className="px-5 pb-4 space-y-2.5 bg-slate-50/30" data-testid={`unassigned-cr-body-${r.id}`}>
                    <div className="p-3 bg-white rounded-sm border border-slate-100">
                      <div className="text-[10px] uppercase tracking-wider text-slate-400 mb-1">Brief</div>
                      <div className="text-sm text-slate-800 whitespace-pre-wrap">{r.brief}</div>
                    </div>
                    <div className="grid grid-cols-2 gap-3 text-xs">
                      <div>
                        <div className="text-[10px] uppercase tracking-wider text-slate-400">Budget</div>
                        <div className="text-slate-700 mt-0.5">{r.budget_hint || "—"}</div>
                      </div>
                      <div>
                        <div className="text-[10px] uppercase tracking-wider text-slate-400">Timeline</div>
                        <div className="text-slate-700 mt-0.5">{r.timeline_hint || "—"}</div>
                      </div>
                    </div>
                    <div className="pt-1 flex flex-col sm:flex-row items-stretch sm:items-center gap-2">
                      <Select value={pick[r.id] || ""} onValueChange={(v) => setPick({ ...pick, [r.id]: v })}>
                        <SelectTrigger className="rounded-sm h-9 text-xs flex-1" data-testid={`unassigned-cr-pick-${r.id}`}>
                          <SelectValue placeholder="Attach to project…" />
                        </SelectTrigger>
                        <SelectContent>
                          {projectOptions.map((p) => (
                            <SelectItem key={p.id} value={p.id}>{p.name}{p.business_unit ? ` · ${p.business_unit}` : ""}</SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                      <Button
                        size="sm"
                        onClick={() => assign(r)}
                        disabled={!pick[r.id] || assigning === r.id}
                        className="rounded-sm h-9 bg-[#F54800] hover:bg-[#D63E00] text-white text-xs"
                        data-testid={`unassigned-cr-assign-${r.id}`}
                      >
                        {assigning === r.id ? "Attaching…" : "Attach"}
                      </Button>
                      <Button
                        size="sm"
                        variant="outline"
                        onClick={() => decline(r)}
                        className="rounded-sm h-9 border-red-200 text-red-700 hover:bg-red-50 text-xs"
                        data-testid={`unassigned-cr-decline-${r.id}`}
                      >
                        Decline
                      </Button>
                    </div>
                  </div>
                )}
              </li>
            );
          })}
        </ul>
      )}
    </div>
  );
}
